import { type Static, Type } from "typebox";

import { VehicleDocumentCategory } from "./documents.js";
import { IssueCategory, IssueSeverity, IssueStatus } from "./enums.js";
import { defineRoute } from "./route.js";
import {
  DateOnly,
  errors,
  Exact,
  Identifier,
  Money,
  Nullable,
  StringEnum,
  Timestamp,
} from "./schema.js";

/**
 * The owner's dashboard (DSH-001): what needs doing, worst first.
 *
 * Everything on it is something a person can act on, and every item carries
 * the path to where it gets fixed. There are no vanity totals: the fleet
 * counts exist only so the page can tell a new company from a quiet one.
 *
 * Semantics are written out in docs/dashboard.md. In short: the whole
 * response is evaluated at one instant (`evaluatedAt`), maintenance is judged
 * by the same due evaluation the fleet page uses, document expiry is judged
 * by `documentStatus` in the organization's zone (`timeZone`), and a section
 * the member may not see is absent rather than zero.
 */

/**
 * Who the page is arranged for. Only the owner's view exists today; role
 * lenses (DSH-004) add values here, and `availableLenses` says which the
 * member may switch to.
 */
export const DashboardLens = StringEnum(["owner"]);
export type DashboardLens = Static<typeof DashboardLens>;

/**
 * One group of work, such as overdue services or open issues. Maintenance is
 * split by due state so each count opens the fleet page filtered to it.
 */
export const DashboardCardKind = StringEnum([
  "maintenance_overdue",
  "maintenance_due",
  "maintenance_needs_baseline",
  "maintenance_upcoming",
  "mileage",
  "issues",
  "documents",
  "out_of_service",
  "drivers_without_vehicle",
  "recalls",
]);
export type DashboardCardKind = Static<typeof DashboardCardKind>;

/**
 * Whether a count can be trusted, so the page can tell "nothing to do" from
 * "nothing to go on".
 *
 * - `ok`: there is work, counted from complete data.
 * - `no_activity`: nothing needs doing, and the data to say so exists.
 * - `missing_data`: nothing can be said yet, such as no schedules set up.
 * - `partial`: counted, but some vehicles could not be judged (see
 *   `explanation`).
 * - `unavailable`: this section failed to load; the rest of the page did not.
 */
export const DashboardDataState = StringEnum([
  "ok",
  "no_activity",
  "missing_data",
  "partial",
  "unavailable",
]);
export type DashboardDataState = Static<typeof DashboardDataState>;

/** The sub-counts a card breaks into, such as issues by severity. */
export const DashboardBreakdownKey = StringEnum([
  "critical",
  "high",
  "medium",
  "low",
  "expired",
  "expiring",
]);
export type DashboardBreakdownKey = Static<typeof DashboardBreakdownKey>;

export const DashboardBreakdown = Exact({
  count: Type.Integer({ minimum: 0 }),
  /** The list filtered to just these, or null when no such list exists. */
  href: Nullable(Type.String()),
  key: DashboardBreakdownKey,
});
export type DashboardBreakdown = Static<typeof DashboardBreakdown>;

export const DashboardCard = Exact({
  /** Distinct vehicles the count touches. */
  affectedVehicles: Type.Integer({ minimum: 0 }),
  breakdown: Type.Array(DashboardBreakdown),
  /** Vehicles, issues, documents or drivers needing action. */
  count: Type.Integer({ minimum: 0 }),
  dataState: DashboardDataState,
  /** Plain words for `missing_data`, `partial` and `unavailable`. */
  explanation: Nullable(Type.String()),
  /** Where the whole group is worked, filtered to it; null when nowhere. */
  href: Nullable(Type.String()),
  kind: DashboardCardKind,
  /** When the longest-waiting item started needing attention, if known. */
  oldestAt: Nullable(Timestamp),
});
export type DashboardCard = Static<typeof DashboardCard>;

export const DashboardItemKind = StringEnum([
  "issue",
  "service_overdue",
  "service_due",
  "baseline_needed",
  "document_expired",
  "document_expiring",
  "reading_needed",
  "out_of_service",
  "driver_without_vehicle",
  "service_upcoming",
  "recall_may_apply",
]);
export type DashboardItemKind = Static<typeof DashboardItemKind>;

export const DashboardVehicle = Exact({
  /** Who has it now, for context: "Dana's van". */
  driverName: Nullable(Type.String()),
  id: Identifier,
  label: Type.String(),
});
export type DashboardVehicle = Static<typeof DashboardVehicle>;

/**
 * One thing to do. The kind says which of the optional figures apply; the
 * rest are null. `href` is the app path where it gets fixed (log the
 * service, open the issue, renew the document), not a section's home page.
 */
export const DashboardItem = Exact({
  daysRemaining: Nullable(Type.Integer()),
  documentCategory: Nullable(VehicleDocumentCategory),
  expiresOn: Nullable(DateOnly),
  href: Type.String(),
  issueCategory: Nullable(IssueCategory),
  issueSeverity: Nullable(IssueSeverity),
  issueStatus: Nullable(IssueStatus),
  /** Unique within the response. */
  key: Type.String(),
  kind: DashboardItemKind,
  milesRemaining: Nullable(Type.Integer()),
  /** Other services needing action on the same vehicle. */
  otherCount: Type.Integer({ minimum: 0 }),
  /** Who is responsible for the next step, when someone is named. */
  ownerName: Nullable(Type.String()),
  /**
   * Why a vehicle is where it is, when something says so: the worst open
   * issue on a vehicle that is out of service.
   */
  reason: Nullable(Type.String()),
  /** Since when it has needed attention, if known. */
  since: Nullable(Timestamp),
  /** The service, issue, document or driver it is about. */
  subject: Type.String(),
  vehicle: Nullable(DashboardVehicle),
});
export type DashboardItem = Static<typeof DashboardItem>;

/** What counts toward billing and what is in the shop, for context. */
export const DashboardFleet = Exact({
  activeVehicles: Type.Integer({ minimum: 0 }),
  /** Active drivers; null when the member may not see drivers. */
  drivers: Nullable(Type.Integer({ minimum: 0 })),
  /** Maintenance schedules in use, paused ones included. */
  maintenanceRules: Type.Integer({ minimum: 0 }),
  outOfServiceVehicles: Type.Integer({ minimum: 0 }),
  /** Vehicles in the working fleet with at least one odometer reading. */
  vehiclesWithReading: Type.Integer({ minimum: 0 }),
});
export type DashboardFleet = Static<typeof DashboardFleet>;

export const DashboardVehicleSpend = Exact({
  amount: Money,
  vehicle: DashboardVehicle,
});
export type DashboardVehicleSpend = Static<typeof DashboardVehicleSpend>;

/**
 * Completed service cost, this calendar month and last, in the fleet's
 * zone. One entry per currency: amounts in different currencies are never
 * added together.
 */
export const DashboardSpend = Exact({
  lastMonth: Type.Array(Money),
  /** The month being reported, "2026-09". */
  month: Type.String({ pattern: "^\\d{4}-\\d{2}$" }),
  serviceCount: Type.Integer({ minimum: 0 }),
  thisMonth: Type.Array(Money),
  /** The vehicles costing most this month, most first. */
  topVehicles: Type.Array(DashboardVehicleSpend),
});
export type DashboardSpend = Static<typeof DashboardSpend>;

export const Dashboard = Exact({
  /** Everything needing action, worst first, capped at `attentionLimit`. */
  attention: Type.Array(DashboardItem),
  /** How many items need action in all, shown or not. */
  attentionTotal: Type.Integer({ minimum: 0 }),
  availableLenses: Type.Array(DashboardLens),
  /** Only the sections the member may see; a hidden one is absent. */
  cards: Type.Array(DashboardCard),
  /** The instant every figure was worked out at: "Last refreshed". */
  evaluatedAt: Timestamp,
  fleet: DashboardFleet,
  lens: DashboardLens,
  /** Present only for members who may see service costs. */
  spend: Type.Optional(DashboardSpend),
  /** The organization's zone, which document expiry and months are read in. */
  timeZone: Type.String(),
  /** Services coming due within the warning window, soonest first. */
  upcoming: Type.Array(DashboardItem),
});
export type Dashboard = Static<typeof Dashboard>;

/** How many items `attention` carries at most. */
export const attentionLimit = 30;

/** How many items `upcoming` carries at most. */
export const upcomingLimit = 5;

export const getDashboard = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/dashboard",
  querystring: Exact({ lens: Type.Optional(DashboardLens) }),
  response: { 200: Dashboard, ...errors(401, 403) },
  tags: ["dashboard"],
});
